import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, getToken, setToken, clearToken, setActiveShopId } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password, remember = false) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, remember });
      setToken(data.token, remember);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      // Message de diagnostic explicite : sans réponse du serveur (réseau,
      // CORS, filtrage) l'ancien message générique n'aidait ni l'utilisateur
      // ni le support à distinguer un mauvais mot de passe d'une panne réseau.
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      let error;
      if (!e.response) error = "Serveur injoignable (réseau). Vérifiez votre connexion puis réessayez.";
      else if (detail != null) error = formatApiError(detail);
      else if (status === 503) error = "Service momentanément saturé. Réessayez dans quelques secondes.";
      else error = `Connexion impossible (erreur ${status}). Réessayez ou contactez le support.`;
      return { ok: false, error };
    }
  };

  const register = async (name, email, password, phone) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password, phone });
      setToken(data.token, false);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const logout = () => {
    clearToken();
    setActiveShopId(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
